import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Paper, Typography, Stack } from '@esgian/esgianui';
import { PortIcon, LocationIcon, VesselIcon } from '@components';
import moment from 'moment';
import { DATE_TIME_FORMAT } from '@constants';
import { getProgressPercentageBetweenTwoDates, formatToDecimalPlaces } from '@helpers';
import { ProfileLink } from '@components/Links';
import VesselAvgSpeedChart from '@components/Sections/ShipAnalytics/VesselProfile/SubSections/VesselActivitySection/VesselAvgSpeedChart';
import { TextWithTooltipIcon } from '@components';
import { getVesselAvgMonthlySpeed } from '@api';
import {
  VESSEL_ACTIVITY_SPEED_RORO_TITLE,
  VESSEL_ACTIVITY_SPEED_MPP_TITLE
} from '@constants/tooltips';
import { getVesselDetailsByFilters } from '@api';
import { useSegment } from '@hooks/useSegment';
import OwnerOrOperatorHistory from '@components/Sections/ShipAnalytics/VesselProfile/SubSections/VesselOverviewSection/OwnerOrOperatorHistory';
import { useQuery } from '@tanstack/react-query';
import { DateRangePicker } from '../../../../../Inputs';
import { useTimezone } from '@hooks/useTimezone';
const progressVesselIconSize = 12;
const progressBarHeight = 2;

let defaultPeriod = {
  start: moment().subtract(6, 'months').startOf('month'),
  end: moment()
};

const OverviewTab = ({
  selectedVesselLatestPosition,
  selectedVesselLatestPortCall,
  selectedVesselNextPortCall
}) => {
  const { isRoRo } = useSegment();
  const { getTimeZoneDisplay } = useTimezone();

  const [speedDates, setSpeedDates] = useState({
    ...defaultPeriod
  });

  const vesselAvgSpeedQuery = useQuery({
    queryKey: ['vesselSpeed', selectedVesselLatestPosition?.imo, speedDates.start, speedDates.end],
    enabled: !!selectedVesselLatestPosition?.imo,
    placeholderData: null,
    queryFn: ({ signal, queryKey }) => {
      return getVesselAvgMonthlySpeed(queryKey[1], queryKey[2], queryKey[3], 2.0, signal)
        .then((result) => result)
        .catch(() => null);
    }
  });

  const vesselDetailsByFiltersQuery = useQuery({
    queryKey: ['vesselDetailsByFilters', parseInt(selectedVesselLatestPosition?.imo)],
    enabled: !!selectedVesselLatestPosition?.imo,
    placeholderData: null,
    queryFn: ({ signal, queryKey }) => {
      return getVesselDetailsByFilters(
        {
          imos: [queryKey[1]]
        },
        signal
      )
        .then((result) => result.vesselInformation[0])
        .catch(() => null);
    }
  });

  const { operatorInformation, ownershipInformation } = vesselDetailsByFiltersQuery?.data || {};

  const { draught, vesselDraught, percentDraught } = selectedVesselLatestPosition ?? {};

  const BASIC_INFO = [
    { label: 'IMO', value: selectedVesselLatestPosition?.imo || '-' },
    { label: 'Build year', value: selectedVesselLatestPosition?.buildYear || '-' },
    {
      label: 'Operator',
      value: (
        <ProfileLink
          name={selectedVesselLatestPosition?.operatorShortName}
          profile={'operator'}
          id={selectedVesselLatestPosition?.operatorId}
          linkVariant="body2"
          textAlign="right"
        />
      )
    }
  ];

  const latestPositionInfo = [
    {
      label: 'Position Received',
      value: selectedVesselLatestPosition?.timestamp
        ? moment(selectedVesselLatestPosition.timestamp).format(DATE_TIME_FORMAT)
        : '-'
    },
    {
      label: 'Speed',
      value: selectedVesselLatestPosition?.speed ? `${selectedVesselLatestPosition.speed} kn` : '-'
    },
    {
      label: 'Draught',
      value: `${formatToDecimalPlaces(draught, 1)} / ${formatToDecimalPlaces(
        vesselDraught,
        1
      )} (${formatToDecimalPlaces(percentDraught, 1)}%)`
    }
  ];

  const progressPercentage =
    getProgressPercentageBetweenTwoDates(
      selectedVesselLatestPortCall?.endDate,
      selectedVesselNextPortCall?.eta
    ) || 0;

  const renderedBasicCard = (
    <Paper sx={{ p: 1 }}>
      <Typography sx={{ fontWeight: 'bold' }}>Basic</Typography>
      <ul style={{ margin: 0, padding: '0 0 0 16px' }}>
        {BASIC_INFO.map(({ label, value }) => (
          <li key={label}>
            <Box display="flex" justifyContent="space-between" gap={1}>
              <Typography variant="body2">{label}</Typography>
              {typeof value === 'string' ||
              typeof value === 'number' ||
              typeof value instanceof String ? (
                <Typography variant="body2" sx={{ textAlign: 'right' }}>
                  {value}
                </Typography>
              ) : (
                value
              )}
            </Box>
          </li>
        ))}
      </ul>
    </Paper>
  );

  const renderedLatestPositionInfoCard = (
    <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography sx={{ fontWeight: 'bold' }}>Latest position info</Typography>

      <Box display="flex" flexDirection="column" gap={0.7}>
        <Box display="flex" justifyContent="space-between">
          <Typography>{selectedVesselLatestPortCall?.portName || '-'}</Typography>
          <Typography sx={{ textAlign: 'right' }}>
            {selectedVesselNextPortCall?.portName || '-'}
          </Typography>
        </Box>
        <Box display="flex" justifyContent="space-between">
          <Typography sx={{ fontSize: '0.8rem' }}>
            {selectedVesselLatestPortCall?.portCode || '-'}
          </Typography>
          <Typography sx={{ fontSize: '0.8rem' }}>
            {selectedVesselNextPortCall?.portUnlocode || '-'}
          </Typography>
        </Box>
        <Box display="flex" gap={1} alignItems="center">
          <PortIcon />
          <Box
            sx={{
              height: progressBarHeight,
              bgcolor: 'grey.100',
              flex: 1,
              position: 'relative'
            }}>
            <Box
              sx={{
                bgcolor: '#2196F3',
                height: progressBarHeight,
                width: `${progressPercentage}%`,
                textAlign: 'right'
              }}
            />
            <Box
              sx={{
                transform: 'rotate(90deg)',
                position: 'absolute',
                top: `-${progressVesselIconSize - progressBarHeight / 2}px`,
                left: `calc(${progressPercentage}% - ${progressVesselIconSize / 2}px)`
              }}>
              <VesselIcon size={progressVesselIconSize} />
            </Box>
          </Box>
          <LocationIcon color="#EACC45" />
        </Box>
        <Box display="flex" justifyContent="space-between">
          <Typography sx={{ fontSize: '0.8rem' }}>
            ATD:{' '}
            {selectedVesselLatestPortCall?.endDate
              ? getTimeZoneDisplay(selectedVesselLatestPortCall.endDate)
              : '-'}
          </Typography>
          <Typography sx={{ fontSize: '0.8rem', textAlign: 'right' }}>
            ETA:{' '}
            {selectedVesselNextPortCall?.eta
              ? getTimeZoneDisplay(selectedVesselNextPortCall.eta)
              : '-'}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: '0.8rem' }}>Latest position info:</Typography>
        <ul style={{ margin: 0, paddingLeft: '16px' }}>
          {latestPositionInfo.map(({ label, value }) => (
            <li key={label}>
              <Box display="flex" justifyContent="space-between">
                <Typography sx={{ fontSize: '0.8rem' }}>{label}:</Typography>
                <Typography sx={{ fontSize: '0.8rem', textAlign: 'right' }}>{value}</Typography>
              </Box>
            </li>
          ))}
        </ul>
      </Box>
    </Paper>
  );

  const renderedAverageSpeedCard = (
    <Paper sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Stack>
          <TextWithTooltipIcon
            label={<Typography sx={{ fontWeight: 'bold' }}>Average speed</Typography>}
            tooltipText={
              isRoRo ? VESSEL_ACTIVITY_SPEED_RORO_TITLE : VESSEL_ACTIVITY_SPEED_MPP_TITLE
            }
            labelVariant={'body1'}
          />
          <Box sx={{ width: '10em' }}>
            <DateRangePicker
              inputVariant={'standard'}
              startDate={speedDates.start}
              endDate={speedDates.end}
              onClose={(start, end) => {
                setSpeedDates({
                  start: start,
                  end: end.isSame(moment(), 'month') ? moment() : end
                });
              }}
            />
          </Box>
        </Stack>
        <VesselAvgSpeedChart
          vesselAvgSpeedData={vesselAvgSpeedQuery.data}
          loading={vesselAvgSpeedQuery.isFetching}
          fileName={
            selectedVesselLatestPosition?.vesselName?.split(' ')?.join('-').toLowerCase() +
            '-average-speed'
          }
        />
      </Stack>
    </Paper>
  );

  const renderedOperatorHistoryCard = (
    <Paper sx={{ p: 2 }}>
      <Stack spacing={2}>
        <OwnerOrOperatorHistory
          ownerOrOperator={'Operator'}
          loading={vesselDetailsByFiltersQuery.isFetching}
          historyData={operatorInformation}
          compact={true}
        />
      </Stack>
    </Paper>
  );

  const renderedOwnerHistoryCard = isRoRo ? (
    <Paper sx={{ p: 2 }}>
      <Stack spacing={2}>
        <OwnerOrOperatorHistory
          ownerOrOperator={'Owner'}
          loading={vesselDetailsByFiltersQuery.isFetching}
          historyData={ownershipInformation}
          compact={true}
        />
      </Stack>
    </Paper>
  ) : null;

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {renderedBasicCard}
      {renderedLatestPositionInfoCard}
      {renderedAverageSpeedCard}
      {renderedOperatorHistoryCard}
      {renderedOwnerHistoryCard}
    </Box>
  );
};

OverviewTab.propTypes = {
  selectedVesselLatestPosition: PropTypes.object,
  selectedVesselLatestPortCall: PropTypes.object,
  selectedVesselNextPortCall: PropTypes.object
};

export default OverviewTab;
